import React, { useEffect, useState, useRef } from "react";
import ReactDatePicker from "react-datepicker";
import { format, parseISO, isValid } from "date-fns"; // Ensure isValid is imported
import "react-datepicker/dist/react-datepicker.css"; // Import the date picker CSS

const DatePicker = ({
    label = "",
    required = false,
    disabled = false,
    placeholder = "Select a date",
    name,
    onChange,
    value, // Expects date in yyyy-MM-dd format or an empty string
    className = "", // Destructure className
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [localValue, setLocalValue] = useState(
        value && typeof value === "string" && isValid(parseISO(value)) ? parseISO(value) : null
    );

    // Ensure the value is correctly handled when passed as yyyy-MM-dd
    useEffect(() => {
        if (value && typeof value === "string") {
            try {
                const parsedDate = parseISO(value); // Convert yyyy-MM-dd string to Date
                if (isValid(parsedDate)) {
                    setLocalValue(parsedDate); // Update local state if valid
                } else {
                    setLocalValue(null); // Reset if invalid
                }
            } catch {
                console.error("Invalid date format provided:", value);
                setLocalValue(null);
            }
        } else {
            setLocalValue(null);
        }
    }, [value]);

    const handleDateChange = (date) => {
        const formattedDate = date ? format(date, "yyyy-MM-dd") : null;

        if (onChange) {
            onChange(formattedDate); // Pass formatted date to parent
        }

        setLocalValue(date); // Update local state
        setIsOpen(false); // Manually close
    };

    return (
        <div className="form-control w-full relative">
            {/* Label */}
            {label && (
                <label
                    htmlFor={props.id || props.name}
                    className="block text-sm font-medium text-gray-700 mb-1"
                >
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}

            {/* ReactDatePicker */}
            <ReactDatePicker
                selected={localValue}
                onChange={handleDateChange}
                onInputClick={() => !disabled && setIsOpen(true)}
                onClickOutside={() => setIsOpen(false)}
                open={isOpen}
                onSelect={() => setIsOpen(false)}
                disabled={disabled}
                placeholderText={placeholder}
                dateFormat="yyyy-MM-dd"
                portalId="root"
                popperClassName="z-[9999991]"
                popperProps={{ strategy: "fixed" }}
                className={`
                    w-full px-4 py-2 text-sm
                    border-2 border-gray-950 shadow-md rounded-lg
                    focus:ring-2 focus:ring-primary/50
                    focus:border-primary
                    transition-all duration-200
                    placeholder:text-gray-400
                    ${disabled ? 'bg-gray-100 cursor-not-allowed text-gray-400' : 'bg-white hover:border-black text-gray-900'}
                    ${className}
                `}
                {...props}
            />
        </div>
    );
};

export default DatePicker;
